"use client";

import { API_BASE_URL } from "@kupikupi/brand";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import {
  payByBalance,
  payOrder,
  payOrderMerge,
  queryAvailableCoupons,
  queryMyAvailableCoupons,
  queryPaymentChannelList,
  queryPaymentResult,
} from "../endpoints/payment";
import type {
  AvailableCoupon,
  MergePayInput,
  MyAvailableCoupon,
  MyAvailableCouponInput,
  OrderPayInput,
  PayByBalanceInput,
  PaymentChannelList,
  PaymentResult,
  PaymentResultStatus,
  PayStatus,
} from "../types/payment";

const PAY_RESULT_KEY = ["payment", "result"] as const;

/** A payStatus is terminal (stop polling) once it is anything but WAITING. */
function isTerminalStatus(status: PayStatus): boolean {
  return status !== "WAITING";
}

const CHANNEL_LIST_KEY = ["payment", "channel-list"] as const;
const COUPON_KEY = ["payment", "available-coupons"] as const;
const MY_COUPON_KEY = ["payment", "my-available-coupons"] as const;
const PAY_STATUS_KEY = ["payment", "pay-status"] as const;

/**
 * #635 补强 — 在进入收银台时一次性查询订单的支付状态。
 * 后端 /channel/list 对已付 D* 订单返回的是 "The order has expired"(不是
 * "already paid"), 因为 createForOrder 在 order.status != WAIT_PAYMENT 时
 * 直接 return null, 导致前端的 "already paid" 错误消息判定永远命中不了。
 *
 * 通过 /payment/result/query/V2 查询 payStatus: SUCCESS 是更可靠的判定——
 * 该接口专门反映支付结果, 不依赖 order.status 是否 WAIT_PAYMENT。
 */
export function usePaymentStatus(authToken: string | null, orderNo: string | null) {
  return useQuery<PaymentResultStatus>({
    queryKey: [...PAY_STATUS_KEY, orderNo],
    enabled: !!authToken && !!orderNo,
    queryFn: () => {
      if (!authToken || !orderNo) throw new Error("NO_TOKEN_OR_ORDER");
      return queryPaymentResult({ baseUrl: API_BASE_URL }, authToken, orderNo);
    },
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    // 一次性查询: 拿到终态就不再重复 fetch (非轮询场景)
    refetchInterval: false,
  });
}

/**
 * Fetch the cashier channel list for an order. `mergePayChannelCode`
 * (balance|goldcoin) requests the slave channels for combined payment.
 */
export function usePaymentChannelList(
  authToken: string | null,
  orderNo: string | null,
  currencyCode: string,
  mergePayChannelCode?: string,
) {
  return useQuery<PaymentChannelList>({
    queryKey: [...CHANNEL_LIST_KEY, orderNo, currencyCode, mergePayChannelCode ?? null],
    enabled: !!authToken && !!orderNo,
    queryFn: () => {
      if (!authToken || !orderNo) throw new Error("NO_TOKEN_OR_ORDER");
      return queryPaymentChannelList(
        { baseUrl: API_BASE_URL },
        authToken,
        mergePayChannelCode
          ? { orderNo, currencyCode, mergePayChannelCode }
          : { orderNo, currencyCode },
      );
    },
    staleTime: 0,
    // #635 — always re-fetch on mount so a fresh page visit re-validates
    // the payment status. bfcache restores are handled separately in
    // PayCashier via a `pageshow` listener that calls refetch() directly.
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
  });
}

export function useAvailableCoupons(authToken: string | null, orderNo: string | null) {
  return useQuery<AvailableCoupon[]>({
    queryKey: [...COUPON_KEY, orderNo],
    enabled: !!authToken && !!orderNo,
    queryFn: () => {
      if (!authToken || !orderNo) throw new Error("NO_TOKEN_OR_ORDER");
      return queryAvailableCoupons({ baseUrl: API_BASE_URL }, authToken, orderNo);
    },
    staleTime: 30_000,
  });
}

/**
 * Pre-order coupon list for confirm-order (#149). Pass the line's goods total
 * (`amount`, major units) so the backend computes each coupon's reduction. Only
 * fetches while `enabled` (e.g. when the picker modal is open) to avoid firing
 * one request per cart item on page load.
 */
export function useMyAvailableCoupons(
  authToken: string | null,
  input: MyAvailableCouponInput,
  enabled = true,
) {
  return useQuery<MyAvailableCoupon[]>({
    queryKey: [...MY_COUPON_KEY, input.orderType, input.currencyCode, input.amount],
    enabled: !!authToken && enabled,
    queryFn: () => {
      if (!authToken) throw new Error("NO_TOKEN");
      return queryMyAvailableCoupons({ baseUrl: API_BASE_URL }, authToken, input);
    },
    staleTime: 30_000,
  });
}

export function usePayByBalance(authToken: string | null) {
  return useMutation<string, Error, PayByBalanceInput>({
    mutationFn: (input) => {
      if (!authToken) throw new Error("NO_TOKEN");
      return payByBalance({ baseUrl: API_BASE_URL }, authToken, input);
    },
  });
}

export function usePayOrder(authToken: string | null) {
  return useMutation<PaymentResult, Error, OrderPayInput>({
    mutationFn: (input) => {
      if (!authToken) throw new Error("NO_TOKEN");
      return payOrder({ baseUrl: API_BASE_URL }, authToken, input);
    },
  });
}

export function usePayOrderMerge(authToken: string | null) {
  return useMutation<PaymentResult, Error, MergePayInput>({
    mutationFn: (input) => {
      if (!authToken) throw new Error("NO_TOKEN");
      return payOrderMerge({ baseUrl: API_BASE_URL }, authToken, input);
    },
  });
}

export interface PaymentResultPollingOptions {
  /** poll cadence in ms (default 3s) */
  intervalMs?: number;
  /** give up after this long; stops polling + flips `timedOut` (default 5min) */
  timeoutMs?: number;
}

export interface PaymentResultPolling {
  data: PaymentResultStatus | undefined;
  /** true once the backend reports payStatus=SUCCESS */
  paid: boolean;
  /** true on a terminal-unpaid status (FAILED / CANCELLED) */
  failed: boolean;
  /** true after the timeout window elapsed with no terminal status */
  timedOut: boolean;
  /** still polling (active interval, not yet terminal, not timed out) */
  polling: boolean;
}

/**
 * Polls GET /payment/result/query/V2 for a third-party order until the backend
 * reports a terminal payStatus or `timeoutMs` elapses.
 *
 * Polling only runs while `orderNo` + `authToken` are set and `enabled` is true,
 * so the caller starts it by setting `enabled` after the third-party redirect is
 * opened, and React Query cancels the interval automatically on unmount. Once a
 * terminal status arrives (`SUCCESS`/`FAILED`/`CANCELLED`/`REFUNDED`) or the
 * deadline passes, the `refetchInterval` returns `false` and polling stops — so
 * it never loops forever.
 */
export function usePaymentResultPolling(
  authToken: string | null,
  orderNo: string | null,
  enabled: boolean,
  options: PaymentResultPollingOptions = {},
): PaymentResultPolling {
  const intervalMs = options.intervalMs ?? 3_000;
  const timeoutMs = options.timeoutMs ?? 5 * 60_000;

  const [timedOut, setTimedOut] = useState(false);
  const deadlineRef = useRef<number | null>(null);

  const active = !!authToken && !!orderNo && enabled && !timedOut;

  // (Re)start the timeout window whenever a fresh polling session begins.
  useEffect(() => {
    if (!active) {
      deadlineRef.current = null;
      return;
    }
    if (deadlineRef.current == null) {
      deadlineRef.current = Date.now() + timeoutMs;
    }
    const remaining = deadlineRef.current - Date.now();
    const id = setTimeout(() => setTimedOut(true), Math.max(0, remaining));
    return () => clearTimeout(id);
  }, [active, timeoutMs]);

  // Reset the timed-out latch when a new order starts polling.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset keyed on orderNo only
  useEffect(() => {
    setTimedOut(false);
    deadlineRef.current = null;
  }, [orderNo]);

  const query = useQuery<PaymentResultStatus>({
    queryKey: [...PAY_RESULT_KEY, orderNo],
    enabled: active,
    queryFn: () => {
      if (!authToken || !orderNo) throw new Error("NO_TOKEN_OR_ORDER");
      return queryPaymentResult({ baseUrl: API_BASE_URL }, authToken, orderNo);
    },
    refetchInterval: (q) => {
      const status = q.state.data?.status;
      if (status && isTerminalStatus(status)) return false;
      if (deadlineRef.current != null && Date.now() >= deadlineRef.current) return false;
      return intervalMs;
    },
    refetchIntervalInBackground: true,
    staleTime: 0,
    gcTime: 0,
  });

  const status = query.data?.status;
  const paid = status === "SUCCESS";
  const failed = status === "FAILED" || status === "CANCELLED";
  const terminal = !!status && isTerminalStatus(status);

  return {
    data: query.data,
    paid,
    failed,
    timedOut,
    polling: active && !terminal,
  };
}
